/**
 * M26 자동 검수 상태 헬퍼 (컴포넌트와 분리해 fast-refresh 경고 회피).
 */

import type { AutomationState, AutomationStatus, InvoiceFileOut, RecordOut } from "@/lib/api/jobs";

const ACTIVE_STATUSES: ReadonlySet<AutomationStatus> = new Set(["queued", "running"]);

/** 자동 검수가 대기 중이거나 진행 중인지 — 화면 자동 새로고침 판단용. */
export function isAutomationActive(record: RecordOut | null | undefined): boolean {
  const status = record?.automation?.status;
  return !!status && ACTIVE_STATUSES.has(status);
}

/** 자동 검수가 고친 칸 하나 — 원래 값 → 고친 값 (빈 값은 ""). */
export interface AutoFixChange {
  key: string;
  before: string;
  after: string;
}

/** 자동 검수가 고친 칸 목록 (자동으로 고친 건이 아니면 빈 배열). */
export function autoFixChanges(automation: AutomationState | null | undefined): AutoFixChange[] {
  if (automation?.status !== "auto_fixed") return [];
  return Object.entries(automation.changed ?? {}).map(([key, change]) => ({
    key,
    before: change?.before == null ? "" : String(change.before),
    after: change?.after == null ? "" : String(change.after),
  }));
}

/** 자동 검수가 값을 고쳤는데 사람이 아직 고친 값을 확인하지 않은 건 — 목록의 "확인 권장". */
export function isAutoFixPending(record: RecordOut | null | undefined): boolean {
  const automation = record?.automation;
  return automation?.status === "auto_fixed" && !automation.checked_at;
}

export type AutomationSummaryKey =
  | "active"
  | "autoPassed"
  | "autoFixPending"
  | "autoFixChecked"
  | "needsHuman"
  | "skipped"
  | "error";

/** 요약 표시 순서. */
export const AUTOMATION_SUMMARY_KEYS: AutomationSummaryKey[] = [
  "active",
  "autoPassed",
  "autoFixPending",
  "autoFixChecked",
  "needsHuman",
  "skipped",
  "error",
];

const SUMMARY_KEY_BY_STATUS: Record<AutomationStatus, AutomationSummaryKey> = {
  queued: "active",
  running: "active",
  auto_passed: "autoPassed",
  auto_fixed: "autoFixPending",
  needs_human: "needsHuman",
  human_owned: "skipped",
  skipped: "skipped",
  error: "error",
};

/** 작업의 건별 자동 검수 상태를 요약 묶음별 건수로 센다 (자동 검수 기록이 없는 건은 제외). */
export function summarizeAutomation(files: InvoiceFileOut[]): Record<AutomationSummaryKey, number> {
  const counts: Record<AutomationSummaryKey, number> = {
    active: 0,
    autoPassed: 0,
    autoFixPending: 0,
    autoFixChecked: 0,
    needsHuman: 0,
    skipped: 0,
    error: 0,
  };
  for (const file of files) {
    const automation = file.record?.automation;
    if (!automation?.status) continue;
    const key =
      automation.status === "auto_fixed" && automation.checked_at
        ? "autoFixChecked"
        : SUMMARY_KEY_BY_STATUS[automation.status];
    counts[key] += 1;
  }
  return counts;
}
